import { gql } from '@apollo/client';

// Account mutations
export const CREATE_ACCOUNT = gql`
  mutation CreateAccount($input: CreateAccountInput!) {
    createAccount(input: $input) {
      id
      name
      createdAt
    }
  }
`;

// WordPress connect: approve a site → mint a short-lived pairing code the
// plugin exchanges (server-to-server) for a durable connection token.
export const CREATE_WP_CONNECTION = gql`
  mutation CreateWpConnection($accountId: ID!, $siteUrl: String!, $siteName: String) {
    createWpConnection(accountId: $accountId, siteUrl: $siteUrl, siteName: $siteName) {
      code
      expiresAt
      accountId
      dataSourceId
    }
  }
`;

export const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $input: UpdateAccountInput!) {
    updateAccount(id: $id, input: $input) {
      id
      name
      isActive
      updatedAt
      urlShortening
      bitlyToken
      firstCommentPrefix
      utmCampaign
      timezone
    }
  }
`;

export const ADD_PAYMENT_METHOD = gql`
  mutation AddPaymentMethod($accountId: ID!, $paymentMethod: String!) {
    updatePaymentMethod(accountId: $accountId, paymentMethod: $paymentMethod) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
      cardBrand
      addedBy
    }
  }
`;

export const MARK_PAYMENT_METHOD_DEFAULT = gql`
  mutation MarkPaymentMethodDefault($accountId: ID!, $paymentMethod: String!) {
    setDefaultPaymentMethod(accountId: $accountId, paymentMethod: $paymentMethod) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
      cardBrand
      addedBy
    }
  }
`;
export const REMOVE_PAYMENT_METHOD = gql`
  mutation RemovePaymentMethod($accountId: ID!, $paymentMethod: String!) {
    deletePaymentMethod(accountId: $accountId, paymentMethod: $paymentMethod) {
      id
      cardLast4
      cardExpMonth
      cardExpYear
      isDefault
      isRemovable
      cardBrand
      addedBy
    }
  }
`;

export const UPDATE_PRODUCT_PLAN = gql`
  mutation UpdateProductPlan(
    $accountId: ID!
    $productPlan: String!
    $billingInterval: String
    $product: String
  ) {
    updateProductPlan(
      accountId: $accountId
      plan: $productPlan
      billingInterval: $billingInterval
      product: $product
    ) {
      id
      name
      createdAt
      plan
      isActive
      techContactEmail
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($accountId: ID!) {
    cancelSubscription(accountId: $accountId) {
      id
      name
      createdAt
      plan
      isActive
      techContactEmail
    }
  }
`;
export const ADD_USER_TO_ACCOUNT = gql`
  mutation AddUserToAccount($input: AddUserToAccountInput!) {
    addUserToAccount(input: $input) {
      user {
        id
        email
      }
      role
      createdAt
    }
  }
`;
export const UPDATE_USER_ROLE = gql`
  mutation UpdateUserRole($accountId: ID!, $id: ID!, $role: String!) {
    updateUserAccount(accountId: $accountId, id: $id, role: $role) {
      id
      role
      accountId
      userId
    }
  }
`;
export const REMOVE_USER_FROM_ACCOUNT = gql`
  mutation RemoveUserFromAccount($accountId: ID!, $id: ID!) {
    removeUserFromAccount(accountId: $accountId, id: $id) {
      id
      role
      accountId
      userId
    }
  }
`;
// Social profile mutations
export const CREATE_SOCIAL_PROFILE = gql`
  mutation CreateSocialProfile($input: CreateSocialProfileInput!) {
    createSocialProfile(input: $input) {
      id
      platform
      profileName
      profileId
      createdAt
      isActive
    }
  }
`;

export const UPDATE_SOCIAL_PROFILE = gql`
  mutation UpdateSocialProfile($id: ID!, $input: UpdateSocialProfileInput!) {
    updateSocialProfile(id: $id, input: $input) {
      id
      platform
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
    }
  }
`;

export const CREATE_DATA_SOURCE = gql`
  mutation CreateDataSource($input: CreateDataSourceInput!) {
    createDataSource(input: $input) {
      id
      name
      type
      url
      createdAt
      isActive
    }
  }
`;

export const UPDATE_DATA_SOURCE = gql`
  mutation UpdateDataSource($id: ID!, $input: UpdateDataSourceInput!) {
    updateDataSource(id: $id, input: $input) {
      id
      name
      type
      url
      isActive
      updatedAt
      pausedAt
      pausedBy
    }
  }
`;

// Connections → WordPress → Unpair (Prototype v18).
export const UNPAIR_WORDPRESS_SITE = gql`
  mutation UnpairWordPressSite($id: ID!) {
    unpairWordPressSite(id: $id)
  }
`;

// Claude Cowork MCP — plaintext clientId/clientSecret returned ONCE.
export const CREATE_COWORK_MCP_CONNECTOR = gql`
  mutation CreateCoworkMcpConnector($accountId: ID!, $label: String, $role: String) {
    createCoworkMcpConnector(accountId: $accountId, label: $label, role: $role) {
      mcpUrl
      clientId
      clientSecret
      tokenPrefix
      dataSourceId
      tokenId
    }
  }
`;

export const ROTATE_COWORK_MCP_CREDENTIALS = gql`
  mutation RotateCoworkMcpCredentials($accountId: ID!, $label: String) {
    rotateCoworkMcpCredentials(accountId: $accountId, label: $label) {
      mcpUrl
      clientId
      clientSecret
      tokenPrefix
      dataSourceId
      tokenId
    }
  }
`;

// Account → AI Agents → Disconnect: the connector's keys and the sign-ins
// bound to them stop working at once; the connector reads as gone until
// someone connects an agent again.
export const DISCONNECT_COWORK_MCP_CONNECTOR = gql`
  mutation DisconnectCoworkMcpConnector($accountId: ID!) {
    disconnectCoworkMcpConnector(accountId: $accountId)
  }
`;

// An agent's Remove (AI Agents, Connections): its secret stops working at once.
export const REMOVE_ACCOUNT_AGENT = gql`
  mutation RemoveAccountAgent($accountId: ID!, $id: ID!) {
    removeAccountAgent(accountId: $accountId, id: $id)
  }
`;

// Atomization config mutations (replace-all)
export const SET_DATA_SOURCE_CUSTOM_FIELDS = gql`
  mutation SetDataSourceCustomFields($dataSourceId: ID!, $fields: [DataSourceCustomFieldInput!]!) {
    setDataSourceCustomFields(dataSourceId: $dataSourceId, fields: $fields) {
      id
      dataSourceId
      fieldName
      sourceKind
      locator
      isActive
      taxonomyKey
      valueDelimiter
    }
  }
`;

export const SET_ACCOUNT_TAXONOMY = gql`
  mutation SetAccountTaxonomy($accountId: ID!, $dimensions: [TaxonomyDimensionInput!]!) {
    setAccountTaxonomy(accountId: $accountId, dimensions: $dimensions) {
      key
      terms
      neverDerive
      isFreeform
    }
  }
`;

export const SET_ACCOUNT_TOPIC_FIELDS = gql`
  mutation SetAccountTopicFields($accountId: ID!, $fields: [AccountTopicFieldInput!]!) {
    setAccountTopicFields(accountId: $accountId, fields: $fields) {
      id
      accountId
      fieldKey
      label
      description
      fieldType
      required
      options
      uiGroup
      sortOrder
      isActive
      isPublic
      aliases
      facetsPublic
    }
  }
`;

// Content review mutations
export const UPDATE_TOPIC = gql`
  mutation UpdateTopic($id: ID!, $input: UpdateTopicInput!) {
    updateTopic(id: $id, input: $input) {
      id
      title
      summary
      mediaUrl
      destinationUrl
      keywords
      publishedDate
      category
    }
  }
`;

export const UPDATE_TOPIC_TAXONOMY = gql`
  mutation UpdateTopicTaxonomy($topicId: ID!, $taxonomies: [TaxonomyValueInput!]!) {
    updateTopicTaxonomy(topicId: $topicId, taxonomies: $taxonomies) {
      id
      taxonomies
    }
  }
`;

export const UPDATE_TOPIC_FACTS = gql`
  mutation UpdateTopicFacts($topicId: ID!, $facts: [VerifiedFactInput!]!) {
    updateTopicFacts(topicId: $topicId, facts: $facts) {
      id
      verifiedFacts
    }
  }
`;

export const UPDATE_TOPIC_ENTITIES = gql`
  mutation UpdateTopicEntities(
    $topicId: ID!
    $keyPeople: [NamedEntityInput!]!
    $keyOrganizations: [NamedEntityInput!]!
    $keyLocations: [NamedEntityInput!]!
  ) {
    updateTopicEntities(
      topicId: $topicId
      keyPeople: $keyPeople
      keyOrganizations: $keyOrganizations
      keyLocations: $keyLocations
    ) {
      id
      keyPeople
      keyOrganizations
      keyLocations
    }
  }
`;

export const SET_CONTENT_TYPE = gql`
  mutation SetContentType($topicId: ID!, $contentType: String!) {
    setContentType(topicId: $topicId, contentType: $contentType) {
      id
      contentType
    }
  }
`;

export const SAVE_CONTENT_ITEM = gql`
  mutation SaveContentItem(
    $id: ID!
    $input: SaveContentItemInput!
    $createVersion: Boolean = true
  ) {
    saveContentItem(id: $id, input: $input, createVersion: $createVersion) {
      id
      title
      summary
      bodyText
      mediaUrl
      destinationUrl
      keywords
      publishedDate
      category
      contentType
      publishStatus
      taxonomies
      customFields
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
    }
  }
`;

export const RESTORE_TOPIC_VERSION = gql`
  mutation RestoreTopicVersion($topicId: ID!, $versionId: ID!) {
    restoreTopicVersion(topicId: $topicId, versionId: $versionId) {
      id
      title
      summary
      bodyText
      mediaUrl
      destinationUrl
      keywords
      publishedDate
      category
      contentType
      publishStatus
      taxonomies
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
    }
  }
`;

export const PUBLISH_TOPIC = gql`
  mutation PublishTopic($id: ID!, $publishedDate: String) {
    publishTopic(id: $id, publishedDate: $publishedDate) {
      id
      publishStatus
      publishedDate
    }
  }
`;

export const SCHEDULE_TOPIC = gql`
  mutation ScheduleTopic($id: ID!, $publishedDate: String!) {
    scheduleTopic(id: $id, publishedDate: $publishedDate) {
      id
      publishStatus
      publishedDate
    }
  }
`;

export const UNPUBLISH_TOPIC = gql`
  mutation UnpublishTopic($id: ID!) {
    unpublishTopic(id: $id) {
      id
      publishStatus
      publishedDate
    }
  }
`;

export const DELETE_TOPIC = gql`
  mutation DeleteTopic($id: ID!) {
    deleteTopic(id: $id) {
      id
      isActive
    }
  }
`;

export const ATOMIZE_TOPIC = gql`
  mutation AtomizeTopic($topicId: ID!) {
    atomizeTopic(topicId: $topicId) {
      id
      taxonomies
      verifiedFacts
      keyPeople
      keyOrganizations
      keyLocations
      atomizedAt
    }
  }
`;

export const SET_DATA_SOURCE_TAXONOMY_SUBSET = gql`
  mutation SetDataSourceTaxonomySubset(
    $dataSourceId: ID!
    $dimensions: [TaxonomyDimensionInput!]!
  ) {
    setDataSourceTaxonomySubset(dataSourceId: $dataSourceId, dimensions: $dimensions) {
      key
      terms
    }
  }
`;

export const REQUEST_POST_DELETION = gql`
  mutation RequestPostDeletion($id: ID!) {
    requestPostDeletion(id: $id) {
      id
      deletionStatus
      updatedAt
    }
  }
`;

export const UNDO_POST_DELETION = gql`
  mutation UndoPostDeletion($id: ID!) {
    undoPostDeletion(id: $id) {
      id
      deletionStatus
      updatedAt
    }
  }
`;

export const CREATE_TOPIC_MUTATION = gql`
  mutation CreateTopic($input: CreateTopicInput!) {
    createTopic(input: $input) {
      id
      title
      summary
      mediaType
      mediaUrl
      destinationUrl
      keywords
      category
      publishedDate
      contentType
      bodyText
      publishStatus
    }
  }
`;

export const CREATE_SETUP_INTENT = gql`
  mutation SetupIntent($email: String, $emailVerification: String) {
    setupIntent(email: $email, emailVerification: $emailVerification) {
      id
      clientSecret
      status
    }
  }
`;

export const SIGNUP_MUTATION = gql`
  mutation Signup(
    $email: String!
    $password: String
    $emailVerification: String
    $accountName: String!
    $paymentName: String
    $paymentMethod: String
    $plan: String!
    $products: [String!]
    $billingInterval: String
    $inviteCode: String
  ) {
    signup(
      email: $email
      password: $password
      emailVerification: $emailVerification
      accountName: $accountName
      paymentName: $paymentName
      paymentMethod: $paymentMethod
      plan: $plan
      products: $products
      billingInterval: $billingInterval
      inviteCode: $inviteCode
    )
  }
`;

// Project mutations
export const CREATE_POST_MUTATION = gql`
  mutation CreateSocialPost($input: CreateSocialPostInput!) {
    createSocialPost(input: $input) {
      id
      title
      summary
      postUrl
      imageUrl
      thoughts
      scheduleTime
      postType
      platform
      socialProfileId
      accountId
      notes
      isDraft
    }
  }
`;

export const UPDATE_POST_MUTATION = gql`
  mutation UpdateSocialPost($id: ID!, $input: UpdateSocialPostInput!) {
    updateSocialPost(id: $id, input: $input) {
      id
      title
      summary
      postUrl
      imageUrl
      postType
      thoughts
      scheduleTime
      notes
      isDraft
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
      accountId
      name
      kind
      plan
      deadline
      startDate
      status
      progress
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($id: ID!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      id
      accountId
      name
      kind
      plan
      deadline
      startDate
      status
      progress
      campaignStory
      landingPageUrl
      landingPageStatus
      color
      notes
      updatedAt
    }
  }
`;

export const DELETE_PROJECT = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id) {
      id
      name
    }
  }
`;

// Project Team Member mutations
export const ADD_PROJECT_TEAM_MEMBER = gql`
  mutation AddProjectTeamMember($input: AddProjectTeamMemberInput!) {
    addProjectTeamMember(input: $input) {
      id
      projectId
      userId
      role
      user {
        id
        email
      }
    }
  }
`;

export const REMOVE_PROJECT_TEAM_MEMBER = gql`
  mutation RemoveProjectTeamMember($id: ID!) {
    removeProjectTeamMember(id: $id) {
      id
    }
  }
`;

export const UPDATE_PROJECT_TEAM_MEMBER = gql`
  mutation UpdateProjectTeamMember($id: ID!, $role: String!) {
    updateProjectTeamMember(id: $id, role: $role) {
      id
      role
    }
  }
`;

// Project Post mutations
export const CREATE_PROJECT_POST = gql`
  mutation CreateProjectPost($input: CreateProjectPostInput!) {
    createProjectPost(input: $input) {
      id
      projectId
      accountId
      title
      content
      status
      assignedTo
      scheduledDate
      dueDate
      postType
      notes
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_PROJECT_POST = gql`
  mutation UpdateProjectPost($id: ID!, $input: UpdateProjectPostInput!) {
    updateProjectPost(id: $id, input: $input) {
      id
      title
      content
      status
      assignedTo
      scheduledDate
      dueDate
      postType
      topicId
      notes
      updatedAt
    }
  }
`;

export const DELETE_PROJECT_POST = gql`
  mutation DeleteProjectPost($id: ID!) {
    deleteProjectPost(id: $id) {
      id
      title
    }
  }
`;

export const PUBLISH_PROJECT_POST_TO_SOCIAL = gql`
  mutation PublishProjectPostToSocial($projectPostId: ID!) {
    publishProjectPostToSocial(projectPostId: $projectPostId) {
      success
      message
      socialPosts {
        id
        title
        platform
        socialProfileId
        accountId
      }
    }
  }
`;

// Project Post Target mutations
export const ADD_PROJECT_POST_TARGET = gql`
  mutation AddProjectPostTarget($input: AddProjectPostTargetInput!) {
    addProjectPostTarget(input: $input) {
      id
      projectPostId
      socialProfileId
      platform
      socialProfile {
        id
        profileName
        platform
      }
    }
  }
`;

export const REMOVE_PROJECT_POST_TARGET = gql`
  mutation RemoveProjectPostTarget($id: ID!) {
    removeProjectPostTarget(id: $id) {
      id
    }
  }
`;

// Research mutations
export const UPSERT_RESEARCH_SETTINGS = gql`
  mutation UpsertResearchSettings($accountId: ID!, $input: UpsertResearchSettingsInput!) {
    upsertResearchSettings(accountId: $accountId, input: $input) {
      id
      accountId
      searchEndpoint
      openaiProvider
      openaiEndpoint
      openaiApiKey
      openaiModel
      isActive
      updatedAt
    }
  }
`;

export const ASSEMBLE_ARTICLE = gql`
  mutation AssembleArticle($accountId: ID!, $input: AssembleArticleInput!) {
    assembleArticle(accountId: $accountId, input: $input) {
      suggestedTitle
      suggestedExcerpt
      sourceWarning
      blocks
      sourceArticles
      selectionInfo
      factIndex
      dedupStats
      verification
      expectationSummary
    }
  }
`;

export const PUBLISH_ASSEMBLED_ARTICLE = gql`
  mutation PublishAssembledArticle($accountId: ID!, $input: PublishAssembledArticleInput!) {
    publishAssembledArticle(accountId: $accountId, input: $input) {
      id
      title
      publishStatus
    }
  }
`;

export const RECORD_RESEARCH_FEEDBACK = gql`
  mutation RecordResearchFeedback(
    $accountId: ID!
    $queryId: ID!
    $feedback: String!
    $reason: String
  ) {
    recordResearchFeedback(
      accountId: $accountId
      queryId: $queryId
      feedback: $feedback
      reason: $reason
    )
  }
`;

export const SUMMARIZE_CONTENT_DRAFT = gql`
  mutation SummarizeContentDraft($accountId: ID!, $title: String, $bodyText: String!) {
    summarizeContentDraft(accountId: $accountId, title: $title, bodyText: $bodyText) {
      bullets
      factCount
    }
  }
`;

// ── Media asset management ──────────────────────────────────────────────────
// NOTE: the two upload mutations below are NO LONGER USED BY THIS APP. Browser
// uploads go through POST /api/upload/asset (see lib/media/uploadAsset) so that
// no page script ever holds a write-capable storage URL, and so uploading does
// not depend on the storage account's CORS rules. They are kept because the
// direct-to-blob path is still the right one for machine clients — the studio
// capture app talks to it server-to-server — and because a document that has
// drifted from its schema is worse than one that is merely unused.

export const CREATE_MEDIA_UPLOAD = gql`
  mutation CreateMediaUpload($accountId: ID!, $input: CreateMediaUploadInput!) {
    createMediaUpload(accountId: $accountId, input: $input) {
      deduped
      asset {
        id
        sha256
        storageKey
        mime
      }
      upload {
        uploadUrl
        method
        headers
        expiresAt
      }
    }
  }
`;

export const FINALIZE_MEDIA_UPLOAD = gql`
  mutation FinalizeMediaUpload($accountId: ID!, $assetId: ID!) {
    finalizeMediaUpload(accountId: $accountId, assetId: $assetId) {
      id
      bytes
      mime
    }
  }
`;

export const RESTORE_MEDIA_RENDITION = gql`
  mutation RestoreMediaRendition($accountId: ID!, $renditionId: ID!) {
    restoreMediaRendition(accountId: $accountId, renditionId: $renditionId) {
      id
      kind
      isCurrent
      url
    }
  }
`;

/** A one-time, resumable upload straight to Cloudflare Stream for one video. */
export const START_VIDEO_UPLOAD = gql`
  mutation StartVideoUpload($accountId: ID!, $sizeBytes: Float!, $name: String) {
    startVideoUpload(accountId: $accountId, sizeBytes: $sizeBytes, name: $name) {
      assetId
      uploadUrl
      expiresAt
      maxDurationSeconds
    }
  }
`;

/** A video's cover: an image from the library, or a moment in the video. */
export const SET_VIDEO_POSTER = gql`
  mutation SetVideoPoster($accountId: ID!, $assetId: ID!, $imageAssetId: ID, $timeSeconds: Float) {
    setVideoPoster(
      accountId: $accountId
      assetId: $assetId
      imageAssetId: $imageAssetId
      timeSeconds: $timeSeconds
    ) {
      id
      video {
        posterUrl
        posterAssetId
        posterTimeSeconds
      }
    }
  }
`;

export const DELETE_MEDIA_ASSET = gql`
  mutation DeleteMediaAsset($accountId: ID!, $assetId: ID!) {
    deleteMediaAsset(accountId: $accountId, assetId: $assetId)
  }
`;

export const LINK_MEDIA = gql`
  mutation LinkMedia($accountId: ID!, $input: LinkMediaInput!) {
    linkMedia(accountId: $accountId, input: $input)
  }
`;

export const UNLINK_MEDIA = gql`
  mutation UnlinkMedia(
    $accountId: ID!
    $ownerType: String!
    $ownerId: ID!
    $role: String!
    $position: Int
  ) {
    unlinkMedia(
      accountId: $accountId
      ownerType: $ownerType
      ownerId: $ownerId
      role: $role
      position: $position
    )
  }
`;

/**
 * Public site identity. Claiming a slug used to require direct SQL; the server
 * validates the format and rejects a slug already taken by another account.
 */
export const UPDATE_PUBLIC_SITE = gql`
  mutation UpdatePublicSite($accountId: ID!, $input: PublicSiteInput!) {
    updatePublicSite(accountId: $accountId, input: $input) {
      id
      name
      publicSlug
      publicEnabled
      publicTitle
      publicTagline
      publicAccessProtected
      publicSearchBlocked
      publicAiCrawlers
    }
  }
`;

/**
 * Choose which attached image represents an item. Pass assetId: null to clear
 * the choice and fall back to role/position inference.
 */
export const SET_PRIMARY_MEDIA = gql`
  mutation SetPrimaryMedia($accountId: ID!, $ownerType: String!, $ownerId: ID!, $assetId: ID) {
    setPrimaryMedia(
      accountId: $accountId
      ownerType: $ownerType
      ownerId: $ownerId
      assetId: $assetId
    ) {
      assetId
      role
      position
      isPrimary
      url
    }
  }
`;

// Copy a hotlinked hero (a feed's image link) into the library and attach it.
export const SAVE_TOPIC_HERO_TO_MEDIA = gql`
  mutation SaveTopicHeroToMedia($accountId: ID!, $topicId: ID!) {
    saveTopicHeroToMedia(accountId: $accountId, topicId: $topicId) {
      assetId
      role
      position
      isPrimary
      url
    }
  }
`;

// ── Collect: capture ────────────────────────────────────────────────────────
// The browser capture client's write path. Keyed by SKU and idempotent, which
// is what makes the offline queue safe to replay: a retried save updates the
// same card rather than adding a second one.

export const SAVE_COLLECTION_ITEM = gql`
  mutation SaveCollectionItem($accountId: ID!, $input: CollectionItemInput!) {
    saveCollectionItem(accountId: $accountId, input: $input) {
      id
      sku
      title
      mediaUrl
      publishStatus
      customFields
      taxonomies
      updatedAt
    }
  }
`;

// ── Collect: capture devices ────────────────────────────────────────────────
// The pairing code comes back ONCE, for display as a QR. Only its hash is
// stored server-side, so it cannot be retrieved again — re-pair to get another.

export const CREATE_CAPTURE_DEVICE = gql`
  mutation CreateCaptureDevice($accountId: ID!, $name: String, $kind: String) {
    createCaptureDevice(accountId: $accountId, name: $name, kind: $kind) {
      pairingCode
      expiresAt
      device {
        id
        name
        kind
        connected
      }
    }
  }
`;

export const REPAIR_CAPTURE_DEVICE = gql`
  mutation RepairCaptureDevice($accountId: ID!, $deviceId: ID!) {
    repairCaptureDevice(accountId: $accountId, deviceId: $deviceId) {
      pairingCode
      expiresAt
    }
  }
`;

export const REVOKE_CAPTURE_DEVICE = gql`
  mutation RevokeCaptureDevice($accountId: ID!, $deviceId: ID!) {
    revokeCaptureDevice(accountId: $accountId, deviceId: $deviceId)
  }
`;

export const APPLY_COLLECTION_TEMPLATE = gql`
  mutation ApplyCollectionTemplate($accountId: ID!, $template: String!, $replace: Boolean) {
    applyCollectionTemplate(accountId: $accountId, template: $template, replace: $replace) {
      template
      fields
      dimensions
    }
  }
`;

// ── Collect: eBay ───────────────────────────────────────────────────────────

export const BEGIN_EBAY_AUTH = gql`
  mutation BeginEbayAuth($accountId: ID!, $returnTo: String) {
    beginEbayAuth(accountId: $accountId, returnTo: $returnTo) {
      url
      expiresInMinutes
    }
  }
`;

export const UPDATE_EBAY_SETTINGS = gql`
  mutation UpdateEbaySettings($accountId: ID!, $input: EbaySettingsInput!) {
    updateEbaySettings(accountId: $accountId, input: $input) {
      accountId
      marketplaceId
      defaultCategoryId
      paymentPolicyId
      returnPolicyId
      fulfillmentPolicyId
      titlePattern
      defaultCondition
      defaultQuantity
    }
  }
`;

export const DISCONNECT_EBAY = gql`
  mutation DisconnectEbay($accountId: ID!) {
    disconnectEbay(accountId: $accountId)
  }
`;

/** Asks the server for a fresh policy snapshot; poll GET_EBAY_POLICIES afterwards. */
export const REFRESH_EBAY_POLICIES = gql`
  mutation RefreshEbayPolicies($accountId: ID!) {
    refreshEbayPolicies(accountId: $accountId)
  }
`;

// ── tinycms: publishing checks + style documents ────────────────────────────

const TINYCMS_SETTINGS_FIELDS = `
  copyEditRequired
  styleGuide
  voiceNotes
  styleGuideUpdatedAt
  voiceNotesUpdatedAt
  canManage
`;

/** Admins only; the server refuses anyone else. */
export const UPDATE_TINYCMS_SETTINGS = gql`
  mutation UpdateTinycmsSettings($accountId: ID!, $input: TinyCmsSettingsInput!) {
    updateTinycmsSettings(accountId: $accountId, input: $input) {
      ${TINYCMS_SETTINGS_FIELDS}
    }
  }
`;

/** Replace the style guide or voice notes (kind: style_guide | voice_notes). */
export const UPDATE_TINYCMS_STYLE_DOCUMENT = gql`
  mutation UpdateTinycmsStyleDocument($accountId: ID!, $kind: TinyCmsStyleKind!, $body: String!) {
    updateTinycmsStyleDocument(accountId: $accountId, kind: $kind, body: $body) {
      ${TINYCMS_SETTINGS_FIELDS}
    }
  }
`;

// ── tinycms admin ───────────────────────────────────────────────────────────

const TINYCMS_ARTICLE_RESULT = `
  id
  slug
  title
  workingHash
  state
  content
  dataset
  publicPriority
  isActive
  updatedAt
`;

export const CREATE_TINYCMS_ARTICLE = gql`
  mutation CreateTinycmsArticle($accountId: ID!, $input: JSON!) {
    createTinycmsArticle(accountId: $accountId, input: $input) {
      ${TINYCMS_ARTICLE_RESULT}
    }
  }
`;

/** Connections → API keys (admins). The token comes back once. */
export const CREATE_API_KEY = gql`
  mutation CreateApiKey($accountId: ID!, $label: String!, $scope: String!, $expiresInDays: Int) {
    createApiKey(
      accountId: $accountId
      label: $label
      scope: $scope
      expiresInDays: $expiresInDays
    ) {
      key {
        id
        label
        prefix
        scope
        createdAt
        lastUsedAt
        expiresAt
      }
      token
    }
  }
`;

export const REVOKE_API_KEY = gql`
  mutation RevokeApiKey($accountId: ID!, $id: ID!) {
    revokeApiKey(accountId: $accountId, id: $id)
  }
`;

/** "Manage in TinyCMS": a feed item becomes a tinycms article (a live one stays live as v1). */
export const ADOPT_TINYCMS_ARTICLE = gql`
  mutation AdoptTinycmsArticle($accountId: ID!, $topicId: ID!) {
    adoptTinycmsArticle(accountId: $accountId, topicId: $topicId) {
      ${TINYCMS_ARTICLE_RESULT}
    }
  }
`;

// The bring-in toast's Undo (QA round 13); workingHash is the one the bring-in returned.
export const UNDO_ADOPT_TINYCMS_ARTICLE = gql`
  mutation UndoAdoptTinycmsArticle($accountId: ID!, $topicId: ID!, $workingHash: String!) {
    undoAdoptTinycmsArticle(accountId: $accountId, topicId: $topicId, workingHash: $workingHash)
  }
`;

export const UPDATE_TINYCMS_ARTICLE = gql`
  mutation UpdateTinycmsArticle($accountId: ID!, $id: ID!, $input: JSON!) {
    updateTinycmsArticle(accountId: $accountId, id: $id, input: $input) {
      ${TINYCMS_ARTICLE_RESULT}
    }
  }
`;

/** The editor's autosave: the same write, without a saved-versions snapshot. */
export const AUTOSAVE_TINYCMS_ARTICLE = gql`
  mutation AutosaveTinycmsArticle($accountId: ID!, $id: ID!, $input: JSON!) {
    updateTinycmsArticle(accountId: $accountId, id: $id, input: $input, createVersion: false) {
      ${TINYCMS_ARTICLE_RESULT}
    }
  }
`;

export const RUN_TINYCMS_COPY_EDIT = gql`
  mutation RunTinycmsCopyEdit($accountId: ID!, $id: ID!) {
    runTinycmsCopyEdit(accountId: $accountId, id: $id)
  }
`;

export const RUN_TINYCMS_DRAFT_COPY_EDIT = gql`
  mutation RunTinycmsDraftCopyEdit($accountId: ID!, $id: ID!, $draft: TinyCmsCopyEditDraft!) {
    runTinycmsDraftCopyEdit(accountId: $accountId, id: $id, draft: $draft)
  }
`;

export const RUN_TINYCMS_DRAFT_PAGE_COPY_EDIT = gql`
  mutation RunTinycmsDraftPageCopyEdit($accountId: ID!, $id: ID!, $page: JSON!) {
    runTinycmsDraftPageCopyEdit(accountId: $accountId, id: $id, page: $page)
  }
`;

export const RUN_TINYCMS_PAGE_COPY_EDIT = gql`
  mutation RunTinycmsPageCopyEdit($accountId: ID!, $id: ID!) {
    runTinycmsPageCopyEdit(accountId: $accountId, id: $id)
  }
`;

/** pageCode: pages run their own code in it (an admin's choice: "Run page code"). */
/** History's See this version: a link to one saved version. */
export const CREATE_TINYCMS_VERSION_PREVIEW = gql`
  mutation CreateTinycmsVersionPreview($accountId: ID!, $kind: String!, $id: ID!, $version: Int!) {
    createTinycmsVersionPreview(accountId: $accountId, kind: $kind, id: $id, version: $version)
  }
`;

export const CREATE_TINYCMS_PREVIEW = gql`
  mutation CreateTinycmsPreview($accountId: ID!, $articleId: ID, $pageId: ID, $pageCode: Boolean) {
    createTinycmsPreview(
      accountId: $accountId
      articleId: $articleId
      pageId: $pageId
      pageCode: $pageCode
    )
  }
`;

export const UPDATE_TINYCMS_SITE = gql`
  mutation UpdateTinycmsSite($accountId: ID!, $input: JSON!) {
    updateTinycmsSite(accountId: $accountId, input: $input)
  }
`;

/** The working site document becomes a copy of the live version; readers see nothing different. */
export const DISCARD_TINYCMS_SITE_CHANGES = gql`
  mutation DiscardTinycmsSiteChanges($accountId: ID!) {
    discardTinycmsSiteChanges(accountId: $accountId)
  }
`;

/** A published version's document back on the working copy; nothing goes live. */
export const RESTORE_TINYCMS_SITE_VERSION = gql`
  mutation RestoreTinycmsSiteVersion($accountId: ID!, $version: Int!) {
    restoreTinycmsSiteVersion(accountId: $accountId, version: $version)
  }
`;

/** Clicking Publish/Schedule is the approval: recorded with the member, now, and this hash. */
export const ACT_ON_TINYCMS_SITE_HEALTH = gql`
  mutation ActOnTinycmsSiteHealth(
    $accountId: ID!
    $suggestionId: ID!
    $action: String!
    $reason: String
  ) {
    actOnTinycmsSiteHealth(
      accountId: $accountId
      suggestionId: $suggestionId
      action: $action
      reason: $reason
    )
  }
`;

export const FIX_TINYCMS_ARTICLE_ADDRESSES = gql`
  mutation FixTinycmsArticleAddresses($accountId: ID!, $ids: [ID!]) {
    fixTinycmsArticleAddresses(accountId: $accountId, ids: $ids)
  }
`;

export const UNDO_TINYCMS_ARTICLE_ADDRESS_FIX = gql`
  mutation UndoTinycmsArticleAddressFix($accountId: ID!, $batchId: ID!) {
    undoTinycmsArticleAddressFix(accountId: $accountId, batchId: $batchId)
  }
`;

export const SET_TINYCMS_HOME_PAGE = gql`
  mutation SetTinycmsHomePage($accountId: ID!, $pageId: ID) {
    setTinycmsHomePage(accountId: $accountId, pageId: $pageId)
  }
`;

export const RUN_TINYCMS_SITE_HEALTH = gql`
  mutation RunTinycmsSiteHealth($accountId: ID!) {
    runTinycmsSiteHealth(accountId: $accountId)
  }
`;

export const PUBLISH_TINYCMS_CONTENT = gql`
  mutation PublishTinycmsContent(
    $accountId: ID!
    $kind: TinyCmsKind!
    $id: ID
    $action: String!
    $contentHash: String!
    $scheduledFor: String
    $overrideReason: String
  ) {
    publishTinycmsContent(
      accountId: $accountId
      kind: $kind
      id: $id
      action: $action
      contentHash: $contentHash
      scheduledFor: $scheduledFor
      overrideReason: $overrideReason
    )
  }
`;

/** History's Restore: a new working copy that needs approval. It never publishes. */
export const RESTORE_TINYCMS_VERSION = gql`
  mutation RestoreTinycmsVersion($accountId: ID!, $kind: String!, $id: ID!, $version: Int!) {
    restoreTinycmsVersion(accountId: $accountId, kind: $kind, id: $id, version: $version)
  }
`;

/** A social post's Restore: the same promise, Reach's own. */
export const RESTORE_SOCIAL_POST_VERSION = gql`
  mutation RestoreSocialPostVersion($id: ID!, $version: Int!) {
    restoreSocialPostVersion(id: $id, version: $version)
  }
`;

/** Approve a social post's waiting version, by its hash. */
export const APPROVE_SOCIAL_POST_VERSION = gql`
  mutation ApproveSocialPostVersion($id: ID!, $version: Int!, $contentHash: String!) {
    approveSocialPostVersion(id: $id, version: $version, contentHash: $contentHash)
  }
`;

export const UNPUBLISH_TINYCMS_CONTENT = gql`
  mutation UnpublishTinycmsContent($accountId: ID!, $kind: TinyCmsKind!, $id: ID, $reason: String) {
    unpublishTinycmsContent(accountId: $accountId, kind: $kind, id: $id, reason: $reason)
  }
`;

/**
 * Pages, the forms they place and optionally the site's look and navigation
 * under one approval: all go live, or none do.
 */
export const PUBLISH_TINYCMS_PAGES = gql`
  mutation PublishTinycmsPages(
    $accountId: ID!
    $pages: [TinyCmsPageVersion!]!
    $forms: [TinyCmsFormVersion!]
    $site: TinyCmsSiteVersion
    $overrideReason: String
  ) {
    publishTinycmsPages(
      accountId: $accountId
      pages: $pages
      forms: $forms
      site: $site
      overrideReason: $overrideReason
    )
  }
`;

/** Set (null: clear) a page's share image on its working copy. */
export const SET_TINYCMS_PAGE_SHARE_IMAGE = gql`
  mutation SetTinycmsPageShareImage($accountId: ID!, $id: ID!, $url: String) {
    setTinycmsPageShareImage(accountId: $accountId, id: $id, url: $url)
  }
`;

/** Delete an article: off the site if live, address freed, restorable for 30 days. */
export const DELETE_TINYCMS_ARTICLE = gql`
  mutation DeleteTinycmsArticle($accountId: ID!, $id: ID!) {
    deleteTinycmsArticle(accountId: $accountId, id: $id)
  }
`;

/** "Not a duplicate": quiets the possible-duplicate flag on this article and its copies. */
export const MARK_TOPIC_NOT_DUPLICATE = gql`
  mutation MarkTopicNotDuplicate($accountId: ID!, $id: ID!) {
    markTopicNotDuplicate(accountId: $accountId, id: $id)
  }
`;

/** Bring back an article deleted in the last 30 days (as a draft, same address). */
export const RESTORE_TINYCMS_ARTICLE = gql`
  mutation RestoreTinycmsArticle($accountId: ID!, $id: ID!) {
    restoreTinycmsArticle(accountId: $accountId, id: $id)
  }
`;

/** Bring back a page deleted in the last 30 days (as a draft, same address). */
export const RESTORE_TINYCMS_PAGE = gql`
  mutation RestoreTinycmsPage($accountId: ID!, $id: ID!) {
    restoreTinycmsPage(accountId: $accountId, id: $id)
  }
`;

/**
 * New page → Build it myself: a draft page from a title, an address ("about",
 * "docs/setup") and a start (text | contact | landing | blank). Returns the page.
 */
export const CREATE_TINYCMS_PAGE = gql`
  mutation CreateTinycmsPage($accountId: ID!, $title: String!, $path: String!, $start: String!) {
    createTinycmsPage(accountId: $accountId, title: $title, path: $path, start: $start)
  }
`;

/** Delete a page: off the site if live, out of the page list (history kept). */
export const DELETE_TINYCMS_PAGE = gql`
  mutation DeleteTinycmsPage($accountId: ID!, $id: ID!) {
    deleteTinycmsPage(accountId: $accountId, id: $id)
  }
`;

/** "Email me new responses" on one form, for the signed-in person (a count, never the content). */
export const SET_TINYCMS_FORM_ALERT = gql`
  mutation SetTinycmsFormAlert($accountId: ID!, $formId: ID!, $on: Boolean!) {
    setTinycmsFormAlert(accountId: $accountId, formId: $formId, on: $on)
  }
`;

/** Delete a form: off the site if live, out of the list; responses kept. Restorable for 30 days. */
export const DELETE_TINYCMS_FORM = gql`
  mutation DeleteTinycmsForm($accountId: ID!, $id: ID!) {
    deleteTinycmsForm(accountId: $accountId, id: $id)
  }
`;

export const RESTORE_TINYCMS_FORM = gql`
  mutation RestoreTinycmsForm($accountId: ID!, $id: ID!) {
    restoreTinycmsForm(accountId: $accountId, id: $id)
  }
`;

// ── tinycms custom domains (admins only) ────────────────────────────────────

export const ADD_TINYCMS_DOMAIN = gql`
  mutation AddTinycmsDomain($accountId: ID!, $hostname: String!) {
    addTinycmsDomain(accountId: $accountId, hostname: $hostname)
  }
`;

export const CHECK_TINYCMS_DOMAIN = gql`
  mutation CheckTinycmsDomain($accountId: ID!, $id: ID!) {
    checkTinycmsDomain(accountId: $accountId, id: $id)
  }
`;

export const REQUEST_TINYCMS_SEARCH_OWNER = gql`
  mutation RequestTinycmsSearchOwner($accountId: ID!, $host: String!, $email: String!) {
    requestTinycmsSearchOwner(accountId: $accountId, host: $host, email: $email)
  }
`;

/** Remove an owner: the engine takes them off the Google property on its next run. */
export const REMOVE_TINYCMS_SEARCH_OWNER = gql`
  mutation RemoveTinycmsSearchOwner($accountId: ID!, $host: String!, $email: String!) {
    removeTinycmsSearchOwner(accountId: $accountId, host: $host, email: $email)
  }
`;

export const SET_PRIMARY_TINYCMS_DOMAIN = gql`
  mutation SetPrimaryTinycmsDomain($accountId: ID!, $id: ID) {
    setPrimaryTinycmsDomain(accountId: $accountId, id: $id)
  }
`;

export const REMOVE_TINYCMS_DOMAIN = gql`
  mutation RemoveTinycmsDomain($accountId: ID!, $id: ID!) {
    removeTinycmsDomain(accountId: $accountId, id: $id)
  }
`;

// ── tinycms analytics & custom code (admins only) ───────────────────────────

export const UPDATE_TINYCMS_INTEGRATIONS = gql`
  mutation UpdateTinycmsIntegrations($accountId: ID!, $input: JSON!, $note: String) {
    updateTinycmsIntegrations(accountId: $accountId, input: $input, note: $note)
  }
`;

export const RESTORE_TINYCMS_INTEGRATIONS = gql`
  mutation RestoreTinycmsIntegrations($accountId: ID!, $version: Int!) {
    restoreTinycmsIntegrations(accountId: $accountId, version: $version)
  }
`;

export const UPDATE_TINYCMS_PAGE_CODE = gql`
  mutation UpdateTinycmsPageCode($accountId: ID!, $pageId: ID!, $input: JSON!, $note: String) {
    updateTinycmsPageCode(accountId: $accountId, pageId: $pageId, input: $input, note: $note)
  }
`;

export const RESTORE_TINYCMS_PAGE_CODE = gql`
  mutation RestoreTinycmsPageCode($accountId: ID!, $pageId: ID!, $version: Int!) {
    restoreTinycmsPageCode(accountId: $accountId, pageId: $pageId, version: $version)
  }
`;

/**
 * Google Analytics 4, read-only (Content → Analytics). start → { url, state }:
 * open url in a popup; its callback page trades the code (exchange →
 * { accountId, ticket, expiresAt, email, properties, suggested }); connect
 * stores the access and queues the first sync. refresh queues a sync.
 */
export const START_GOOGLE_ANALYTICS_CONNECT = gql`
  mutation StartGoogleAnalyticsConnect($accountId: ID!) {
    startGoogleAnalyticsConnect(accountId: $accountId)
  }
`;

export const EXCHANGE_GOOGLE_ANALYTICS_CODE = gql`
  mutation ExchangeGoogleAnalyticsCode($code: String!, $state: String!) {
    exchangeGoogleAnalyticsCode(code: $code, state: $state)
  }
`;

export const CONNECT_GOOGLE_ANALYTICS = gql`
  mutation ConnectGoogleAnalytics($accountId: ID!, $ticket: String!, $propertyId: String!) {
    connectGoogleAnalytics(accountId: $accountId, ticket: $ticket, propertyId: $propertyId)
  }
`;

export const DISCONNECT_GOOGLE_ANALYTICS = gql`
  mutation DisconnectGoogleAnalytics($accountId: ID!) {
    disconnectGoogleAnalytics(accountId: $accountId)
  }
`;

export const REFRESH_SITE_ANALYTICS = gql`
  mutation RefreshSiteAnalytics($accountId: ID!) {
    refreshSiteAnalytics(accountId: $accountId)
  }
`;

// ── tinycms licensed-feed keys (admins only, never over MCP) ────────────────

/** input: { label, collections?, tags?, expiresAt? }. Returns { key, ...view } — the key ONCE. */
export const CREATE_TINYCMS_FEED_KEY = gql`
  mutation CreateTinycmsFeedKey($accountId: ID!, $input: JSON!) {
    createTinycmsFeedKey(accountId: $accountId, input: $input)
  }
`;

/** Change access: who a key is for and what it reads, on the same key. */
export const UPDATE_TINYCMS_FEED_KEY = gql`
  mutation UpdateTinycmsFeedKey($accountId: ID!, $id: ID!, $input: JSON!) {
    updateTinycmsFeedKey(accountId: $accountId, id: $id, input: $input)
  }
`;

/** Signs the person in to a site's sign-in sections: { url } on that site. */
export const CREATE_TINYCMS_READER_LINK = gql`
  mutation CreateTinycmsReaderLink($site: String!, $next: String, $origin: String) {
    createTinycmsReaderLink(site: $site, next: $next, origin: $origin)
  }
`;

/** Permanent: a revoked key stops working at once. */
export const REVOKE_TINYCMS_FEED_KEY = gql`
  mutation RevokeTinycmsFeedKey($accountId: ID!, $id: ID!) {
    revokeTinycmsFeedKey(accountId: $accountId, id: $id)
  }
`;

// ── tinycms verification & ads.txt (admins only; live at once) ─────────────

/** input: { verifications? (replaces the list), adsTxt?, appAdsTxt? (null clears) }. */
export const UPDATE_TINYCMS_ROOT_FILES = gql`
  mutation UpdateTinycmsRootFiles($accountId: ID!, $input: JSON!, $note: String) {
    updateTinycmsRootFiles(accountId: $accountId, input: $input, note: $note)
  }
`;

export const RESTORE_TINYCMS_ROOT_FILES = gql`
  mutation RestoreTinycmsRootFiles($accountId: ID!, $version: Int!) {
    restoreTinycmsRootFiles(accountId: $accountId, version: $version)
  }
`;

/** Turn an add-on the account holds off or back on (today: atomizer). */
export const SET_PRODUCT_ENABLED = gql`
  mutation SetProductEnabled($accountId: ID!, $product: String!, $enabled: Boolean!) {
    setProductEnabled(accountId: $accountId, product: $product, enabled: $enabled) {
      id
      autoAtomizeEnabled
      products {
        product
        status
        plan
      }
    }
  }
`;

export const BILLING_PORTAL_URL = gql`
  mutation BillingPortalUrl($accountId: ID!) {
    billingPortalUrl(accountId: $accountId)
  }
`;

/** Mark a form's responses read: the ids given, or all of them. Returns how many changed. */
export const MARK_TINYCMS_FORM_RESPONSES_READ = gql`
  mutation MarkTinycmsFormResponsesRead($accountId: ID!, $formId: ID!, $ids: [ID!]) {
    markTinycmsFormResponsesRead(accountId: $accountId, formId: $formId, ids: $ids)
  }
`;

/** Delete one form response for good. */
export const DELETE_TINYCMS_FORM_RESPONSE = gql`
  mutation DeleteTinycmsFormResponse($accountId: ID!, $id: ID!) {
    deleteTinycmsFormResponse(accountId: $accountId, id: $id)
  }
`;

/** Form settings (admins): { retentionMonths, consent }. */
export const UPDATE_TINYCMS_FORM_SETTINGS = gql`
  mutation UpdateTinycmsFormSettings($accountId: ID!, $input: JSON!) {
    updateTinycmsFormSettings(accountId: $accountId, input: $input)
  }
`;

/**
 * Website › Pages editing (backlog 8ffdec3d; apiserver pageEditing.js): the
 * editor's own preview link shows the page and/or header and footer as typed.
 * Nothing is saved. { ok, path } (ok false, reason "not_ready", before the
 * server's migration is applied).
 */
export const SET_TINYCMS_EDIT_PREVIEW = gql`
  mutation SetTinycmsEditPreview(
    $accountId: ID!
    $token: String!
    $pageId: ID
    $page: JSON
    $site: JSON
  ) {
    setTinycmsEditPreview(
      accountId: $accountId
      token: $token
      pageId: $pageId
      page: $page
      site: $site
    )
  }
`;

/** Save draft: the working copy takes the edits. { pageId, workingHash, siteHash }. */
export const SAVE_TINYCMS_EDITS = gql`
  mutation SaveTinycmsEdits($accountId: ID!, $pageId: ID, $page: JSON, $site: JSON) {
    saveTinycmsEdits(accountId: $accountId, pageId: $pageId, page: $page, site: $site)
  }
`;
